import type { QueryClient } from "@tanstack/react-query";
import { getRouteById } from "../domain/capabilities";
import { getRoleDefinition, isCoachingRole, type CoachingRole } from "../domain/roles";
import { coachingQueryKeys } from "../hooks/coaching-query-keys";
import { setCoachingProfileHint } from "./coaching-org-context";
import type { CoachingMembershipDto, CoachingProfileDto } from "./coaching-dtos";

export type SwitchableCoachingMembership = Omit<CoachingMembershipDto, "role" | "status"> & Partial<Pick<CoachingMembershipDto, "role" | "status">> & {
  coachingRole?: CoachingRole | null;
};

/**
 * Apply the selected organization to the live transport context, remove all
 * organization-scoped query data, and only then navigate to its landing route.
 */
export async function switchCoachingOrganization({
  membership,
  queryClient,
  profile,
  navigate,
}: {
  membership: SwitchableCoachingMembership;
  queryClient: QueryClient;
  profile?: CoachingProfileDto;
  navigate: (path: string) => void;
}) {
  const role = isCoachingRole(membership.coachingRole) ? membership.coachingRole : null;
  setCoachingProfileHint(membership.orgId, role);
  queryClient.removeQueries({
    queryKey: coachingQueryKeys.root,
    predicate: (query) => query.queryKey[1] !== "profile",
  });
  queryClient.setQueryData(coachingQueryKeys.profile(), (current: CoachingProfileDto | undefined) =>
    current
      ? {
          ...current,
          orgId: membership.orgId,
          ...(role ? { role } : {}),
        }
      : profile
        ? {
            ...profile,
            orgId: membership.orgId,
            ...(role ? { role } : {}),
          }
        : current
  );
  const landingRoute = role ? getRouteById(getRoleDefinition(role).defaultRouteId) : null;
  navigate(landingRoute?.path ?? "/dashboard");
}
